import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Minus, Undo2, Redo2,
  ChevronDown, ArrowLeft, Save, Loader2,
  Highlighter, Pilcrow,
} from "lucide-react";
import { type DocumentTemplate, type DocumentTemplateType } from "@/lib/api";
import { TEMPLATE_TYPES } from "./TemplateFormModal";
import { useTranslation } from "react-i18next";

// ── Placeholder hints ──────────────────────────────────────────────────────────

const PLACEHOLDERS = [
  { labelKey: "employeeName", value: "{employee_name}" },
  { labelKey: "startDate", value: "{start_date}" },
  { labelKey: "position", value: "{position}" },
  { labelKey: "salary", value: "{salary}" },
  { labelKey: "companyName", value: "{company_name}" },
  { labelKey: "todayDate", value: "{today_date}" },
];

const FONT_SIZES = ["8", "9", "10", "11", "12", "14", "16", "18", "20", "24", "28", "32", "36", "48"];

// ── Toolbar separator ──────────────────────────────────────────────────────────

function Sep() {
  return <div className="w-px h-5 bg-border mx-0.5 shrink-0" />;
}

// ── Toolbar button ─────────────────────────────────────────────────────────────

function ToolBtn({
  active, disabled, onClick, title, children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center justify-center h-7 w-7 rounded text-sm transition-colors shrink-0
        ${active
          ? "bg-primary/15 text-primary"
          : "text-foreground/70 hover:bg-muted hover:text-foreground"
        }
        ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      {children}
    </button>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface DocumentTemplateEditorProps {
  template?: DocumentTemplate | null;
  onSave: (data: { name: string; templateType: DocumentTemplateType; contentText: string }) => void;
  onCancel: () => void;
  isSaving: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function DocumentTemplateEditor({
  template,
  onSave,
  onCancel,
  isSaving,
}: DocumentTemplateEditorProps) {
  const { t } = useTranslation();
  const isEdit = !!template;

  const [name, setName] = useState(template?.name ?? "");
  const [templateType, setTemplateType] = useState<DocumentTemplateType>(
    template?.templateType ?? "contract",
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      FontSize,
      Highlight.configure({ multicolor: false }),
    ],
    content: template?.contentText ?? "<p></p>",
    editorProps: {
      attributes: {
        class: "outline-none min-h-[600px] leading-relaxed text-foreground",
        style: "font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 12pt;",
        spellcheck: "true",
      },
    },
  });

  const handleSave = useCallback(() => {
    if (!name.trim()) return;
    const html = editor?.getHTML() ?? "";
    onSave({ name: name.trim(), templateType, contentText: html });
  }, [name, templateType, editor, onSave]);

  const insertPlaceholder = useCallback(
    (value: string) => {
      editor?.chain().focus().insertContent(value).run();
    },
    [editor],
  );

  if (!editor) return null;

  const headingLevel = editor.isActive("heading", { level: 1 })
    ? "1"
    : editor.isActive("heading", { level: 2 })
    ? "2"
    : editor.isActive("heading", { level: 3 })
    ? "3"
    : "0";

  return (
    <div className="flex flex-col h-full -mt-2">
      {/* ── Top bar ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-background border-b border-border">
        <Button variant="ghost" size="sm" onClick={onCancel} className="gap-1.5 shrink-0">
          <ArrowLeft className="h-4 w-4" />
          {t("documents.templates.backToList")}
        </Button>

        <div className="h-5 w-px bg-border" />

        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("documents.templates.namePlaceholder")}
          className="h-8 max-w-xs text-sm font-medium"
        />

        <Select
          value={templateType}
          onValueChange={(v) => setTemplateType(v as DocumentTemplateType)}
        >
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEMPLATE_TYPES.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !name.trim()}
          className="gap-1.5 shrink-0"
        >
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {isEdit
            ? t("documents.templates.saveChanges")
            : t("documents.templates.createAction")}
        </Button>
      </div>

      {/* ── Formatting toolbar ───────────────────────────────────── */}
      <div className="flex items-center gap-0.5 px-3 py-1.5 bg-white dark:bg-card border-b border-border flex-wrap">
        {/* Undo / Redo */}
        <ToolBtn
          title={t("documents.templates.toolbar.undo")}
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.redo")}
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        {/* Paragraph / Heading style */}
        <Select
          value={headingLevel}
          onValueChange={(v) => {
            if (v === "0") editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: Number(v) as 1 | 2 | 3 }).run();
          }}
        >
          <SelectTrigger className="h-7 w-28 text-xs border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">
              <span className="flex items-center gap-1.5">
                <Pilcrow className="h-3 w-3" /> {t("documents.templates.toolbar.paragraph")}
              </span>
            </SelectItem>
            <SelectItem value="1"><span className="font-bold text-base">{t("documents.templates.toolbar.heading1")}</span></SelectItem>
            <SelectItem value="2"><span className="font-semibold text-sm">{t("documents.templates.toolbar.heading2")}</span></SelectItem>
            <SelectItem value="3"><span className="font-medium text-xs">{t("documents.templates.toolbar.heading3")}</span></SelectItem>
          </SelectContent>
        </Select>

        {/* Font size */}
        <Select
          value=""
          onValueChange={(v) => {
            (editor.chain().focus() as any).setFontSize(`${v}pt`).run();
          }}
        >
          <SelectTrigger className="h-7 w-16 text-xs border-border ml-1">
            <SelectValue placeholder="12pt" />
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZES.map((size) => (
              <SelectItem key={size} value={size}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Sep />

        {/* Bold / Italic / Underline / Strike */}
        <ToolBtn
          title={t("documents.templates.toolbar.bold")}
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.italic")}
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.underline")}
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.strikethrough")}
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        {/* Text colour */}
        <label
          className="flex items-center justify-center h-7 w-7 rounded cursor-pointer text-foreground/70 hover:bg-muted transition-colors relative"
          title={t("documents.templates.toolbar.textColor")}
        >
          <span className="text-xs font-bold select-none" style={{ fontFamily: "serif" }}>A</span>
          <input
            type="color"
            className="absolute opacity-0 w-0 h-0"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            defaultValue="#000000"
          />
        </label>

        {/* Highlight */}
        <ToolBtn
          title={t("documents.templates.toolbar.highlight")}
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        {/* Alignment */}
        <ToolBtn
          title={t("documents.templates.toolbar.alignLeft")}
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.alignCenter")}
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.alignRight")}
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          <AlignRight className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.justify")}
          active={editor.isActive({ textAlign: "justify" })}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        >
          <AlignJustify className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        {/* Lists */}
        <ToolBtn
          title={t("documents.templates.toolbar.bulletList")}
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn
          title={t("documents.templates.toolbar.numberedList")}
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        {/* Horizontal rule */}
        <ToolBtn
          title={t("documents.templates.toolbar.horizontalRule")}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <Minus className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        {/* Insert placeholder */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1 h-7 px-2 rounded text-xs font-medium text-primary border border-primary/30 hover:bg-primary/5 transition-colors shrink-0"
            >
              {"{…}"} <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {PLACEHOLDERS.map((ph) => (
              <DropdownMenuItem
                key={ph.value}
                onClick={() => insertPlaceholder(ph.value)}
                className="flex flex-col items-start gap-0"
              >
                <span className="font-mono text-xs text-primary">{ph.value}</span>
                <span className="text-[11px] text-muted-foreground">
                  {t(`documents.templates.placeholders.${ph.labelKey}`)}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ── Page canvas ──────────────────────────────────────────── */}
      <div
        className="flex-1 overflow-y-auto py-8 px-4"
        style={{ backgroundColor: "#e8e8e8" }}
        onClick={() => editor.commands.focus()}
      >
        <div
          className="mx-auto bg-white shadow-[0_2px_8px_rgba(0,0,0,0.15),0_8px_32px_rgba(0,0,0,0.08)] rounded-sm"
          style={{
            width: "794px",
            minHeight: "1123px",
            padding: "96px 90px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* ── Scoped editor styles ─────────────────────────────────── */}
      <style>{`
        .tiptap h1 { font-size: 2em; font-weight: 700; margin: 0.67em 0; line-height: 1.2; }
        .tiptap h2 { font-size: 1.5em; font-weight: 600; margin: 0.75em 0; line-height: 1.3; }
        .tiptap h3 { font-size: 1.17em; font-weight: 600; margin: 0.83em 0; line-height: 1.4; }
        .tiptap p  { margin: 0.5em 0; }
        .tiptap ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
        .tiptap ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        .tiptap li { margin: 0.25em 0; }
        .tiptap hr { border: none; border-top: 1px solid #d1d5db; margin: 1.5em 0; }
        .tiptap blockquote { border-left: 3px solid #d1d5db; padding-left: 1em; color: #6b7280; margin: 1em 0; }
        .tiptap mark { background-color: #fef08a; padding: 0.1em 0.2em; border-radius: 2px; }
      `}</style>
    </div>
  );
}
